import { db } from "./dbClient";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import jsPDF from "jspdf";
import "jspdf/dist/jspdf.umd.min.js";

const STATUS_COLORS = {
  pending:   "#F59E0B",
  approved:  "#10B981",
  rejected:  "#EF4444",
  expired:   "#6B7280",
};

const RISK_COLORS = {
  critical: "#EF4444",
  high:     "#F59E0B",
  medium:   "#3B82F6",
  low:      "#10B981",
};

const EXCEPTION_TYPES = [
  "CDD Simplified Due Diligence",
  "Transaction Monitoring Rule Bypass",
  "Onboarding Approval Override",
  "Sanction Screen Exception",
  "EDD Deferral",
  "CTR Filing Extension",
  "Customer Risk Override",
  "Policy Deviation",
  "Other",
];

const BLANK = {
  exception_type: "",
  subject_name: "",
  rationale: "",
  risk_level: "medium",
  requested_duration_days: 30,
  supporting_evidence: "",
};

export default function ExceptionManagementPage({ company, currentUser, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  const isMLRO = ["admin", "compliance_officer", "supervisor"].includes(currentUser?.permission);

  useEffect(() => {
    load();
  }, [company?.id]);

  async function load() {
    if (!company?.id) return;
    setLoading(true);
    try {
      const { data, error } = await db
        .from("policy_exceptions")
        .select("*")
        .eq("company_id", company.id)
        .order("created_at", false);
      if (error) {
        console.error('[Exceptions] Load error:', error.message);
        setExceptions([]);
      } else {
        setExceptions(data || []);
      }
    } catch (e) {
      console.error('[Exceptions] Load failed:', e.message);
      setExceptions([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!draft.exception_type || !draft.subject_name || !draft.rationale) return;
    setSaving(true);
    try {
      const payload = {
        ...draft,
        company_id: company.id,
        requested_by: `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email,
        requested_by_email: currentUser.email,
        status: "pending",
        expires_at: new Date(Date.now() + draft.requested_duration_days * 86400000).toISOString(),
      };
      const { data, error } = await db.from("policy_exceptions").insert(payload);
      if (error) {
        console.error('[Exceptions] Create error:', error.message);
      } else {
        // Insert succeeded - log the action
        await log({
          action: "exception_requested",
          module: "exception_management",
          entityId: payload.id || `exception-${Date.now()}`,
          entityName: `${draft.exception_type} for ${draft.subject_name}`,
          meta: { exceptionType: draft.exception_type, riskLevel: draft.risk_level }
        });
        setShowCreate(false);
        setDraft({ ...BLANK });
        // Reload to show the new exception
        await load();
      }
    } catch (e) {
      console.error('[Exceptions] Create failed:', e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleReview(decision) {
    if (!selected) return;
    setReviewing(true);
    try {
      const updates = {
        status: decision,
        reviewed_by: `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email,
        reviewed_by_email: currentUser.email,
        reviewed_at: new Date().toISOString(),
        review_note: reviewNote,
      };
      const { data, error } = await db.from("policy_exceptions").update(updates).eq("id", selected.id);
      if (error) {
        console.error('[Exceptions] Review error:', error.message);
      } else {
        await log({
          action: `exception_${decision}`,
          module: "exception_management",
          entityId: selected.id,
          entityName: `${selected.exception_type} for ${selected.subject_name}`,
          meta: { decision, reviewNote }
        });
        setReviewNote("");
        // Reload to ensure fresh data
        await load();
        setSelected(null);
      }
    } catch (e) {
      console.error('[Exceptions] Review failed:', e.message);
    } finally {
      setReviewing(false);
    }
  }

  async function handleRevoke(exc) {
    try {
      const { data, error } = await db.from("policy_exceptions").update({ status: "expired", review_note: "Manually revoked by admin" }).eq("id", exc.id);
      if (error) {
        console.error('[Exceptions] Revoke error:', error.message);
        return;
      }
      await log({
        action: "exception_revoked",
        module: "exception_management",
        entityId: exc.id,
        entityName: `${exc.exception_type} for ${exc.subject_name}`,
        meta: { reason: "Manually revoked by admin" }
      });
      // Reload to ensure fresh data
      await load();
    } catch (e) {
      console.error('[Exceptions] Revoke failed:', e.message);
    }
  }

  function exportToPDF() {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 10;
    const lineHeight = 6;
    const margin = 10;
    const maxWidth = pageWidth - 2 * margin;

    // Title
    doc.setFontSize(18);
    doc.text("Policy Exceptions Report", margin, yPos);
    yPos += 12;
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPos);
    yPos += 8;

    exceptions.forEach((exc, idx) => {
      if (yPos > pageHeight - 25) {
        doc.addPage();
        yPos = 10;
      }

      doc.setFont(undefined, "bold");
      doc.text(`${idx + 1}. ${exc.exception_type}`, margin, yPos);
      yPos += lineHeight;

      doc.setFont(undefined, "normal");
      doc.text(`Subject: ${exc.subject_name}`, margin + 3, yPos);
      yPos += lineHeight;
      doc.text(`Status: ${exc.status}`, margin + 3, yPos);
      yPos += lineHeight;
      doc.text(`Risk Level: ${exc.risk_level}`, margin + 3, yPos);
      yPos += lineHeight;
      doc.text(`Requested Duration: ${exc.requested_duration_days} days`, margin + 3, yPos);
      yPos += lineHeight;
      doc.text(`Requested: ${new Date(exc.created_at).toLocaleDateString()}`, margin + 3, yPos);
      yPos += lineHeight;

      if (exc.rationale) {
        const rationale = doc.splitTextToSize(`Rationale: ${exc.rationale}`, maxWidth - 3);
        rationale.forEach(line => {
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = 10;
          }
          doc.text(line, margin + 3, yPos);
          yPos += lineHeight;
        });
      }

      if (exc.review_note) {
        const reviewNote = doc.splitTextToSize(`Review Note: ${exc.review_note}`, maxWidth - 3);
        reviewNote.forEach(line => {
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = 10;
          }
          doc.text(line, margin + 3, yPos);
          yPos += lineHeight;
        });
      }

      if (exc.reviewed_by_email) {
        doc.text(`Reviewed by: ${exc.reviewed_by_email}`, margin + 3, yPos);
        yPos += lineHeight;
      }

      yPos += 4;
    });

    doc.save(`Exceptions-Report-${new Date().toISOString().split('T')[0]}.pdf`);
  }

  // KPI counts
  const pending  = exceptions.filter(e => e.status === "pending").length;
  const approved = exceptions.filter(e => e.status === "approved").length;
  const rejected = exceptions.filter(e => e.status === "rejected").length;
  const critical = exceptions.filter(e => e.risk_level === "critical" || e.risk_level === "high").length;

  const filtered = filterStatus === "all" ? exceptions : exceptions.filter(e => e.status === filterStatus);

  const F = {
    card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 },
    label: { color: C.textDim, fontSize: 11, letterSpacing: "0.08em", fontFamily: "'DM Mono',monospace", marginBottom: 6, display: "block" },
    input: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "8px 12px", fontSize: 13, fontFamily: "'DM Mono',monospace", outline: "none", width: "100%", boxSizing: "border-box" },
    btn: (col) => ({ padding: "8px 18px", background: col, border: "none", borderRadius: 7, color: C.bg, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 700 }),
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 700, margin: "0 0 4px" }}>{t('exception.title')}</h1>
          <p style={{ color: C.textDim, fontSize: 13, margin: 0 }}>{t('exception.subtitle')}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={exportToPDF} style={F.btn("#8B5CF6")}>⬇ Export PDF</button>
          <button onClick={() => setShowCreate(true)} style={F.btn(C.accent)}>+ {t('exception.requestException')}</button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 28 }}>
        {[
          { label: "Pending Review", value: pending, color: STATUS_COLORS.pending },
          { label: "Approved Active", value: approved, color: STATUS_COLORS.approved },
          { label: "Rejected", value: rejected, color: STATUS_COLORS.rejected },
          { label: "High / Critical Risk", value: critical, color: RISK_COLORS.high },
        ].map(k => (
          <div key={k.label} style={F.card}>
            <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>{k.label.toUpperCase()}</div>
            <div style={{ color: k.color, fontSize: 28, fontWeight: 700 }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 380px" : "1fr", gap: 20 }}>
        {/* List */}
        <div style={F.card}>
          {/* Filter tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {["all", "pending", "approved", "rejected", "expired"].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                style={{ padding: "5px 14px", background: filterStatus === s ? C.accent : C.surface, border: `1px solid ${filterStatus === s ? C.accent : C.border}`, borderRadius: 20, color: filterStatus === s ? C.bg : C.textMid, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace", fontWeight: filterStatus === s ? 700 : 400, textTransform: "capitalize" }}>
                {s === "all" ? `All (${exceptions.length})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${exceptions.filter(e => e.status === s).length})`}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ color: C.textDim, fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: "32px 0", textAlign: "center" }}>No exceptions found.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filtered.map(exc => {
                const isExpired = exc.status === "approved" && exc.expires_at && new Date(exc.expires_at) < new Date();
                const effectiveStatus = isExpired ? "expired" : exc.status;
                return (
                  <div key={exc.id} onClick={() => setSelected(exc)}
                    style={{ background: selected?.id === exc.id ? `${C.accent}15` : C.bg, border: `1px solid ${selected?.id === exc.id ? C.accent : C.border}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", transition: "all 0.15s" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                          <span style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{exc.exception_type}</span>
                          <span style={{ background: `${RISK_COLORS[exc.risk_level]}20`, color: RISK_COLORS[exc.risk_level], fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase" }}>{exc.risk_level}</span>
                        </div>
                        <div style={{ color: C.textMid, fontSize: 12 }}>Subject: {exc.subject_name}</div>
                        <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
                          Requested by {exc.requested_by} · {exc.created_at ? new Date(exc.created_at).toLocaleDateString() : ""}
                        </div>
                      </div>
                      <span style={{ background: `${STATUS_COLORS[effectiveStatus]}20`, color: STATUS_COLORS[effectiveStatus], fontSize: 10, padding: "3px 10px", borderRadius: 10, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, textTransform: "uppercase" }}>
                        {effectiveStatus}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{ ...F.card, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>Exception Detail</span>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>×</button>
            </div>
            {[
              ["Type", selected.exception_type],
              ["Subject", selected.subject_name],
              ["Risk Level", selected.risk_level],
              ["Status", selected.status],
              ["Requested By", selected.requested_by],
              ["Requested", selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"],
              ["Expires", selected.expires_at ? new Date(selected.expires_at).toLocaleDateString() : "—"],
              ["Reviewed By", selected.reviewed_by || "Pending"],
              ["Reviewed At", selected.reviewed_at ? new Date(selected.reviewed_at).toLocaleString() : "—"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", paddingBottom: 8, borderBottom: `1px solid ${C.border}30` }}>
                <span style={{ color: C.textDim, fontSize: 12 }}>{k}</span>
                <span style={{ color: k === "Risk Level" ? RISK_COLORS[v] : k === "Status" ? STATUS_COLORS[v] || C.text : C.text, fontSize: 12, fontWeight: k === "Risk Level" || k === "Status" ? 700 : 400, textTransform: k === "Risk Level" || k === "Status" ? "capitalize" : "none" }}>{v}</span>
              </div>
            ))}
            <div>
              <span style={F.label}>RATIONALE</span>
              <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6, background: C.bg, borderRadius: 7, padding: "10px 12px" }}>{selected.rationale || "—"}</div>
            </div>
            {selected.supporting_evidence && (
              <div>
                <span style={F.label}>SUPPORTING EVIDENCE</span>
                <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6, background: C.bg, borderRadius: 7, padding: "10px 12px" }}>{selected.supporting_evidence}</div>
              </div>
            )}
            {selected.review_note && (
              <div>
                <span style={F.label}>REVIEW NOTE</span>
                <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6, background: C.bg, borderRadius: 7, padding: "10px 12px" }}>{selected.review_note}</div>
              </div>
            )}

            {/* MLRO review actions */}
            {isMLRO && selected.status === "pending" && (
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                <span style={F.label}>REVIEW DECISION</span>
                <textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)}
                  placeholder="Add review notes or conditions…" rows={3}
                  style={{ ...F.input, resize: "vertical", marginBottom: 10 }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => handleReview("approved")} disabled={reviewing}
                    style={{ ...F.btn(C.success), flex: 1 }}>{reviewing ? "…" : "Approve"}</button>
                  <button onClick={() => handleReview("rejected")} disabled={reviewing}
                    style={{ ...F.btn(C.danger), flex: 1 }}>{reviewing ? "…" : "Reject"}</button>
                </div>
              </div>
            )}

            {/* Revoke active exception */}
            {isMLRO && selected.status === "approved" && new Date(selected.expires_at) > new Date() && (
              <button onClick={() => handleRevoke(selected)}
                style={{ ...F.btn("#6B7280"), marginTop: 4 }}>Revoke Exception</button>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: 520, maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Request Policy Exception</span>
              <button onClick={() => setShowCreate(false)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 20 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={F.label}>EXCEPTION TYPE *</label>
                <select value={draft.exception_type} onChange={e => setDraft(d => ({ ...d, exception_type: e.target.value }))}
                  style={{ ...F.input }}>
                  <option value="">Select type…</option>
                  {EXCEPTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={F.label}>SUBJECT NAME / REFERENCE *</label>
                <input value={draft.subject_name} onChange={e => setDraft(d => ({ ...d, subject_name: e.target.value }))}
                  placeholder="Customer name, transaction ID, or account reference…"
                  style={F.input} />
              </div>
              <div>
                <label style={F.label}>RISK LEVEL</label>
                <select value={draft.risk_level} onChange={e => setDraft(d => ({ ...d, risk_level: e.target.value }))}
                  style={{ ...F.input }}>
                  {["low", "medium", "high", "critical"].map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={F.label}>RATIONALE *</label>
                <textarea value={draft.rationale} onChange={e => setDraft(d => ({ ...d, rationale: e.target.value }))}
                  placeholder="Explain why this exception is being requested and the business justification…"
                  rows={4} style={{ ...F.input, resize: "vertical" }} />
              </div>
              <div>
                <label style={F.label}>SUPPORTING EVIDENCE</label>
                <textarea value={draft.supporting_evidence} onChange={e => setDraft(d => ({ ...d, supporting_evidence: e.target.value }))}
                  placeholder="Reference document IDs, case numbers, or additional context…"
                  rows={3} style={{ ...F.input, resize: "vertical" }} />
              </div>
              <div>
                <label style={F.label}>REQUESTED DURATION (DAYS)</label>
                <input type="number" min={1} max={365} value={draft.requested_duration_days}
                  onChange={e => setDraft(d => ({ ...d, requested_duration_days: parseInt(e.target.value) || 30 }))}
                  style={{ ...F.input, width: 100 }} />
              </div>
              <div style={{ background: `${C.warning || "#F59E0B"}15`, border: `1px solid ${C.warning || "#F59E0B"}40`, borderRadius: 8, padding: "10px 14px", color: C.warning || "#F59E0B", fontSize: 11 }}>
                This request will be sent to your MLRO / Compliance Officer for review. Exception requests are logged in the audit trail.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setShowCreate(false)}
                  style={{ flex: 1, padding: "10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
                  Cancel
                </button>
                <button onClick={handleCreate} disabled={saving || !draft.exception_type || !draft.subject_name || !draft.rationale}
                  style={{ flex: 1, padding: "10px", background: draft.exception_type && draft.subject_name && draft.rationale ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
                  {saving ? "Submitting…" : "Submit Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
